import { useContext, useMemo, useState } from 'react';

import { faSensor } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Typography } from '@mui/material';
import { RadioGroupOptions, RadioGroupVariants, SearchInput } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import { DeviceTypes } from 'src/constants/DeviceTypes';
import { Door } from 'src/store/sageAdminApi';
import { getDevicesFromAllocation } from 'src/utils/getDevicesFromAllocation';
import { numberAwareComparator } from 'src/utils/listUtils';

export interface SelectDoorDrawerProps extends DrawerComponentProps {
    doors: Door[];
    preSelectedDoorId?: string;
}

const SearchPlaceholder = 'Search by door name';

function SelectDoorDrawer(props: SelectDoorDrawerProps) {
    const { doors, preSelectedDoorId, onNext, title = 'Select Door' } = props;
    const { trackEvent, trackSearchInput } = useContext(AnalyticsContext);
    const [selectedDoorId, setSelectedDoorId] = useState(preSelectedDoorId || '');
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputAnalytics = trackSearchInput('Select Door Drawer', SearchPlaceholder);

    const isDoorSelectable = (door: Door) => {
        // A door is selectable if:
        // - It has no device allocation (new door), OR
        // - It is an offline door and does not already have a DoorAlarm device assigned
        if (!door.deviceAllocation) return true;

        const isOfflineDoor = door.deviceAllocation.allocationType === 'OFFLINE_DOOR';
        const { devicesOfDeviceType: doorSensors } = getDevicesFromAllocation(
            door.deviceAllocation,
            DeviceTypes.DoorAlarm
        );

        return isOfflineDoor && doorSensors.length === 0;
    };

    const filteredDoors = useMemo(() => {
        if (searchTerm) {
            return doors?.filter((door) =>
                door?.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return doors;
    }, [doors, searchTerm]);

    const sortedDoors = useMemo(() => {
        if (!filteredDoors) return [];
        return filteredDoors.sort((a, b) => {
            if (isDoorSelectable(a) !== isDoorSelectable(b)) {
                return isDoorSelectable(a) ? -1 : 1;
            }

            return numberAwareComparator(a.name, b.name);
        });
    }, [filteredDoors]);

    const handleSelectDoor = (doorId: string) => {
        trackEvent('Select Door Clicked', { ObjectType: ObjectTypes.Button, door: doorId });
        setSelectedDoorId(doorId);
    };

    const handleContinue = () => {
        trackEvent('Continue Clicked', { ObjectType: ObjectTypes.Button, selectedDoorId });
        onNext({ value: selectedDoorId });
    };

    return (
        <>
            {/* Fixed Header */}
            <DrawerTitle title={title}>
                <SearchInput
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    placeholder={SearchPlaceholder}
                    {...searchInputAnalytics}
                />
            </DrawerTitle>
            {/* Scrolling Area */}
            <DrawerBody>
                {sortedDoors?.length > 0 && (
                    <RadioGroupOptions
                        radioGroupProps={{ 'aria-label': 'unit' }}
                        variant={RadioGroupVariants.Outlined}
                        selectedOption={selectedDoorId}
                        onSelectOption={handleSelectDoor}
                        options={sortedDoors?.map((door) => ({
                            disabled: !isDoorSelectable(door),
                            value: door.rid,
                            label: (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: isDoorSelectable(door) ? 'grey.900' : 'grey.500',
                                        }}
                                    >
                                        {door.name}
                                    </Typography>

                                    {!isDoorSelectable(door) && (
                                        <FontAwesomeIcon icon={faSensor} size="lg" />
                                    )}
                                </Box>
                            ),
                        }))}
                    />
                )}

                {filteredDoors?.length === 0 && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'grey.700',
                        }}
                    >
                        {!!searchTerm ? 'No doors found' : 'No doors'}
                    </Typography>
                )}
            </DrawerBody>
            {/* Bottom Sticky Buttons */}
            <DrawerFooter>
                <Button
                    color="primary"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={!selectedDoorId}
                    onClick={handleContinue}
                >
                    Continue
                </Button>
            </DrawerFooter>
        </>
    );
}

export default SelectDoorDrawer;
